import { isMaskedValue, maskedValuesForDisplay, hasSensitiveValues, maskSensitiveText } from './parameterMasking';
import { PARAMETER_DATA_TYPES } from 'src/constants/constants';

export const TEMPLATE_DRAFT_SCHEMA_VERSION = 2;
export const TEMPLATE_FIELD_TYPES = ['number', 'string', 'time', 'datetime', 'boolean', 'json'];
export const TEMPLATE_FIELD_TYPE_LABELS = {
  string: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  time: 'Time',
  datetime: 'Date',
  json: 'JSON',
};
export const MAX_TEMPLATE_TAB_DEPTH = 2;
export const DEFAULT_TEMPLATE_VERSION = '1.0';
export const TEMPLATE_VERSION_PATTERN = /^\d+(\.\d+)*$/;

/** The immutable part of a template version ("2" for "2.3.1"). */
export const templateMajorVersion = (version) => String(version ?? '').split('.')[0];

const normalizeTemplateVersion = (version) => (
  typeof version === 'string' && version.trim() ? version.trim() : DEFAULT_TEMPLATE_VERSION
);

export const defaultValueForTemplateType = (type) => {
  switch (type) {
    case 'number': return 0;
    case 'boolean': return false;
    case 'json': return {};
    default: return '';
  }
};

export const getDataTypeValue = (dataType) =>
  typeof dataType === 'number' ? dataType : dataType?.value;

export const isTemplateParameter = (parameterOrDataType) => {
  const dataType = parameterOrDataType?.data_type ?? parameterOrDataType;
  return getDataTypeValue(dataType) === PARAMETER_DATA_TYPES.TEMPLATE;
};

const normalizeSnakeCaseInput = (value = '') => value
  .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
  .replace(/[^a-zA-Z0-9]+/g, '_')
  .toLowerCase();

export const toSnakeCaseInput = (value = '') => normalizeSnakeCaseInput(value);

export const toSnakeCase = (value = '') => normalizeSnakeCaseInput(value)
  .replace(/^_+|_+$/g, '');

export const toTemplateId = (value = '') => toSnakeCase(value).toUpperCase();

export const createTemplateDocument = (name = '') => ({
  version: DEFAULT_TEMPLATE_VERSION,
  template_id: toTemplateId(name) || 'TEMPLATE',
  type: 'template',
  children: [],
});

/** Snake-cased `preferred`, numbered (`_2`, `_3`, …) until it is unique among `children`. */
export const makeUniqueKey = (children, preferred) => {
  const base = toSnakeCase(preferred) || 'node';
  const used = new Set((children || []).map((item) => item.key));
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
};

export const createTemplateTab = (children = [], name = 'New Tab') => {
  const key = makeUniqueKey(children, name);
  return {
    key,
    human_readable_name: name,
    type: 'tab',
    order: children.length + 1,
    children: [],
  };
};

export const createTemplateField = (children = [], type = 'string', name = 'New Field') => {
  const safeType = TEMPLATE_FIELD_TYPES.includes(type) ? type : 'string';
  const key = makeUniqueKey(children, name);
  const common = {
    key,
    human_readable_name: name,
    type: safeType,
    default_value: defaultValueForTemplateType(safeType),
    is_sensitive: false,
  };
  if (safeType === 'number') {
    return { ...common, validation: {} };
  }
  return common;
};

const pickDefined = (source, fields) => fields.reduce((result, field) => (
  source?.[field] === undefined ? result : { ...result, [field]: source[field] }
), {});

const withDefaultValue = (type, value) => {
  if (value === undefined) return defaultValueForTemplateType(type);
  return value;
};

const sanitizeTemplateNodes = (nodes = []) => nodes.flatMap((sourceNode, index) => {
  if (!sourceNode || typeof sourceNode !== 'object' || Array.isArray(sourceNode)) return [];
  if (sourceNode.type === 'tab') {
    const tab = pickDefined(sourceNode, ['key', 'human_readable_name', 'description', 'order']);
    return [{
      ...tab,
      key: tab.key || `tab_${index + 1}`,
      human_readable_name: tab.human_readable_name || tab.key || `Tab ${index + 1}`,
      type: 'tab',
      order: Number.isInteger(tab.order) ? tab.order : index + 1,
      children: sanitizeTemplateNodes(sourceNode.children),
    }];
  }

  const type = sourceNode.type;
  if (!TEMPLATE_FIELD_TYPES.includes(type)) return [];
  const field = pickDefined(sourceNode, ['key', 'human_readable_name', 'description', 'is_sensitive']);
  const sanitized = {
    is_sensitive: false,
    ...field,
    key: field.key || `field_${index + 1}`,
    human_readable_name: field.human_readable_name || field.key || `Field ${index + 1}`,
    type,
    default_value: withDefaultValue(type, sourceNode.default_value),
  };
  if (type === 'number') {
    const min = sourceNode.validation?.min;
    const max = sourceNode.validation?.max;
    const validation = {
      ...(typeof min === 'number' && Number.isFinite(min) && { min }),
      ...(typeof max === 'number' && Number.isFinite(max) && { max }),
    };
    if (Object.keys(validation).length) sanitized.validation = validation;
  }
  return [sanitized];
});

/** Produces the backend-allowlisted template payload. */
export const sanitizeTemplateDocument = (document) => ({
  version: normalizeTemplateVersion(document?.version),
  template_id: document?.template_id ?? 'TEMPLATE',
  type: 'template',
  children: sanitizeTemplateNodes(document?.children),
});

export const isSupportedTemplateDocument = (document) => Boolean(
  document
  && typeof document.template_id === 'string'
  && document.template_id.trim()
  && document.type === 'template'
  && Array.isArray(document.children),
);

export const countTemplateNodes = (documentOrNodes) => {
  const nodes = Array.isArray(documentOrNodes)
    ? documentOrNodes
    : documentOrNodes?.children || [];
  return nodes.reduce(
    (count, node) => count + 1 + (node.type === 'tab' ? countTemplateNodes(node.children) : 0),
    0,
  );
};

export const templateSummary = (document) => {
  if (!document || typeof document !== 'object') return '—';
  const count = countTemplateNodes(document);
  return `Template · ${count} node${count === 1 ? '' : 's'}`;
};

const countTemplateLeaves = (nodes = []) => nodes.reduce((counts, node) => {
  if (!node || typeof node !== 'object') return counts;
  if (node.type !== 'tab') return { ...counts, fields: counts.fields + 1 };
  const nested = countTemplateLeaves(node.children);
  return { fields: counts.fields + nested.fields, tabs: counts.tabs + 1 + nested.tabs };
}, { fields: 0, tabs: 0 });

/**
 * What identifies a template document to an operator: its id, version and
 * size. Returns null for anything that is not a supported template document
 * so callers can fall back to their empty state.
 */
export const templateIdentity = (document) => {
  if (!isSupportedTemplateDocument(document)) return null;
  const version = typeof document.version === 'string' ? document.version.trim() : '';
  const { fields, tabs } = countTemplateLeaves(document.children);
  return {
    templateId: document.template_id.trim(),
    version: version || null,
    fieldCount: fields,
    tabCount: tabs,
  };
};

export const formatParameterValue = (value, template = false, sensitive = false) => {
  if (sensitive) return maskSensitiveText(value) ?? '—';
  if (isMaskedValue(value)) return value.display;
  if (value == null) return '—';
  if (typeof value === 'boolean') return String(value);
  if (typeof value !== 'object') return value;
  return template ? templateSummary(value) : JSON.stringify(maskedValuesForDisplay(value));
};

export const pathKey = (path = []) => path.join('.');

const canonicalize = (value) => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value)
    .sort()
    .reduce((result, key) => ({ ...result, [key]: canonicalize(value[key]) }), {});
};

export const createTemplateDraftBaseSignature = (values) =>
  JSON.stringify(canonicalize(values ?? {}));

export const createPromiseQueue = () => {
  let tail = Promise.resolve();
  return (task) => {
    const result = tail.catch(() => undefined).then(task);
    tail = result;
    return result;
  };
};

export const getNodeAtPath = (document, path = []) => {
  let children = document?.children;
  let node = null;
  for (const key of path) {
    node = (children || []).find((item) => item.key === key);
    if (!node) return null;
    children = node.children;
  }
  return node;
};

export const getChildrenAtPath = (document, parentPath = []) => {
  if (parentPath.length === 0) return document?.children || [];
  const parent = getNodeAtPath(document, parentPath);
  return parent?.type === 'tab' ? parent.children || [] : [];
};

const normalizeOrders = (nodes = []) => nodes.map((node, index) => {
  if (node.type !== 'tab') return node;
  return {
    ...node,
    order: index + 1,
    children: normalizeOrders(node.children || []),
  };
});

export const normalizeTemplateOrders = (document) => ({
  ...document,
  children: normalizeOrders(document?.children || []),
});

const replaceChildren = (document, parentPath, nextChildren) => {
  if (parentPath.length === 0) {
    return normalizeTemplateOrders({ ...document, children: nextChildren });
  }
  const [key, ...rest] = parentPath;
  return normalizeTemplateOrders({
    ...document,
    children: (document.children || []).map((node) => {
      if (node.key !== key || node.type !== 'tab') return node;
      if (rest.length === 0) return { ...node, children: nextChildren };
      const nestedDocument = replaceChildren({ children: node.children || [] }, rest, nextChildren);
      return { ...node, children: nestedDocument.children };
    }),
  });
};

export const updateNodeAtPath = (document, path, updater) => {
  if (!path?.length) return document;
  const [key, ...rest] = path;
  const children = document.children || [];
  const index = children.findIndex((node) => node.key === key);
  if (index < 0) return document;
  const node = children[index];
  if (rest.length && node.type !== 'tab') return document;
  const updated = rest.length ? updateNodeAtPath(node, rest, updater) : updater(node);
  if (updated === node) return document;
  const nextChildren = [...children];
  nextChildren[index] = updated;
  // Value and metadata edits preserve imported order; structural operations
  // continue to normalize order through replaceChildren.
  return { ...document, children: nextChildren };
};

export const addNodeAtPath = (document, parentPath, node) => {
  const siblings = getChildrenAtPath(document, parentPath);
  return replaceChildren(document, parentPath, [...siblings, node]);
};

export const removeNodeAtPath = (document, path) => {
  const parentPath = path.slice(0, -1);
  const key = path[path.length - 1];
  const siblings = getChildrenAtPath(document, parentPath);
  return replaceChildren(document, parentPath, siblings.filter((node) => node.key !== key));
};

// Descendants are copied verbatim (minus the legacy id) so a duplicated tab
// keeps the same field keys as its source: carrier sub-profiles must share
// update paths, and only the top-level clone needs a unique key.
const copySubtree = (node) => {
  const { id: _id, ...copy } = node;
  if (node.type === 'tab') copy.children = (node.children || []).map(copySubtree);
  return copy;
};

const cloneNode = (node, siblings, { key, human_readable_name: name } = {}) => ({
  ...copySubtree(node),
  key: makeUniqueKey(siblings, key || `${node.key}_copy`),
  human_readable_name: name || `${node.human_readable_name || node.key} Copy`,
});

/**
 * Inserts one clone per `copies` entry right after the node, in order. Each
 * entry may override the clone's key and display name; keys are still made
 * unique against the siblings (including the earlier copies).
 */
export const duplicateNodeAtPath = (document, path, copies = [{}]) => {
  const parentPath = path.slice(0, -1);
  const siblings = getChildrenAtPath(document, parentPath);
  const node = getNodeAtPath(document, path);
  if (!node || !copies.length) return document;
  const next = [...siblings];
  copies.forEach((overrides, offset) => {
    const index = next.findIndex((item) => item.key === node.key);
    next.splice(index + 1 + offset, 0, cloneNode(node, next, overrides));
  });
  return replaceChildren(document, parentPath, next);
};

export const reorderNodesAtPath = (document, parentPath, fromIndex, toIndex) => {
  const siblings = [...getChildrenAtPath(document, parentPath)];
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= siblings.length || toIndex >= siblings.length) {
    return document;
  }
  const [moved] = siblings.splice(fromIndex, 1);
  siblings.splice(toIndex, 0, moved);
  return replaceChildren(document, parentPath, siblings);
};

export const moveNode = (document, sourcePath, targetParentPath) => {
  const node = getNodeAtPath(document, sourcePath);
  if (!node || pathKey(targetParentPath).startsWith(`${pathKey(sourcePath)}.`)) return document;
  const tabHeight = (currentNode) => {
    if (currentNode?.type !== 'tab') return 0;
    return 1 + Math.max(0, ...(currentNode.children || []).map(tabHeight));
  };
  if (targetParentPath.length + tabHeight(node) > MAX_TEMPLATE_TAB_DEPTH) return document;
  const targetChildren = getChildrenAtPath(document, targetParentPath);
  if (targetChildren.some((item) => item.key === node.key)) return document;
  const withoutSource = removeNodeAtPath(document, sourcePath);
  return addNodeAtPath(withoutSource, targetParentPath, node);
};

export const hasDuplicateSiblingKeys = (nodes = []) => {
  const keys = new Set();
  for (const node of nodes) {
    if (!node.key || keys.has(node.key)) return true;
    keys.add(node.key);
    if (node.type === 'tab' && hasDuplicateSiblingKeys(node.children || [])) return true;
  }
  return false;
};

export const listTemplateTabs = (document) => {
  const result = [{ path: [], label: 'Template root' }];
  const visit = (nodes, parentPath, parentLabels) => {
    nodes.forEach((node) => {
      if (node.type !== 'tab') return;
      const path = [...parentPath, node.key];
      const labels = [...parentLabels, node.human_readable_name || node.key];
      result.push({ path, label: labels.join(' / ') });
      visit(node.children || [], path, labels);
    });
  };
  visit(document?.children || [], [], []);
  return result;
};

const REQUEST_FAILED = { path: [], code: 'request_failed', detail: 'Request failed.' };

// Backend template errors are formatted "<dotted.path|root>: detail"; node
// keys are snake_case so the first colon is the separator (a "HH:mm" hint in
// the detail is never mistaken for it).
const TEMPLATE_ERROR_PREFIX = /^([a-zA-Z0-9_.[\]]+):\s+(.+)$/s;

const normalizeOneTemplateError = (item) => {
  if (!item || typeof item !== 'object') return REQUEST_FAILED;
  const message = item.detail || item.message || item.title;
  const normalizedMessage = typeof message === 'object' ? Object.values(message).join(' ') : message || 'Request failed.';
  const code = item.code || 'request_failed';
  if (Array.isArray(item.path)) return { path: item.path, code, detail: normalizedMessage };
  const prefixMatch = code === 'request_failed' ? null : normalizedMessage.match(TEMPLATE_ERROR_PREFIX);
  if (!prefixMatch || prefixMatch[1] === 'root') return { path: [], code, detail: normalizedMessage };
  return { path: prefixMatch[1].split('.'), code, detail: prefixMatch[2] };
};

/**
 * Every error of a failed template request as [{path, code, detail}]. The
 * node path is parsed from the backend's "<path>: detail" prefix (root errors
 * keep the "root:" label); always returns at least one entry.
 */
export const normalizeTemplateErrors = (error) => {
  const errors = error?.response?.data?.errors ?? error?.errors ?? error;
  if (Array.isArray(errors)) {
    return errors.length ? errors.map(normalizeOneTemplateError) : [REQUEST_FAILED];
  }
  if (errors && typeof errors === 'object') {
    return [{
      path: Array.isArray(errors.path) ? errors.path : [],
      code: errors.code || 'request_failed',
      detail: errors.detail || errors.message || 'Request failed.',
    }];
  }
  return [REQUEST_FAILED];
};

/** First normalized error of a failed template request. */
export const normalizeTemplateError = (error) => normalizeTemplateErrors(error)[0];

const quoted = (detail, fallback = '') => detail?.match(/'([^']*)'/)?.[1] ?? fallback;

const numberIn = (detail) => detail?.match(/-?\d+(?:\.\d+)?/)?.[0];

const limitCopy = (min, max) => {
  if (min != null && max != null) return `Must be between ${min} and ${max}`;
  if (min != null) return `Must be at least ${min}`;
  if (max != null) return `Must be at most ${max}`;
  return null;
};

const boundsCopy = ({ node, error }, bound) => {
  const limits = node?.validation || {};
  return limitCopy(limits.min, limits.max) || limitCopy(
    bound === 'min' ? numberIn(error.detail) : undefined,
    bound === 'max' ? numberIn(error.detail) : undefined,
  ) || 'Out of the allowed range';
};

/**
 * Operator wording per template error code. Each entry receives
 * `{ error, node, document, immutableRoot }` (node = the node the error
 * resolves to, when any) and returns the message shown under the control.
 */
export const TEMPLATE_ERROR_COPY = {
  min_value: (context) => boundsCopy(context, 'min'),
  max_value: (context) => boundsCopy(context, 'max'),
  invalid_number: () => 'Use a whole number',
  invalid_string: () => 'Use text',
  invalid_time: () => 'Use HH:mm, e.g. 08:30',
  invalid_datetime: () => 'Use yyyyMMdd',
  invalid_boolean: () => 'Use true or false',
  invalid_json: () => 'Use a JSON object, e.g. {"retry_count": 3}',
  duplicate_key: ({ error }) => {
    const path = error.path || [];
    const key = quoted(error.detail, path[path.length - 1] || '');
    const scope = path.length > 1 ? 'in this tab' : 'at the template root';
    return `Another node ${scope} already uses key "${key}"`;
  },
  key_required: () => 'Key is required',
  name_required: () => 'Display name is required',
  immutable_major_version: ({ document, immutableRoot }) => {
    const current = templateMajorVersion(immutableRoot?.version);
    const next = templateMajorVersion(document?.version);
    return current && next
      ? `Version ${next}.x cannot replace ${current}.x; bump the minor part`
      : 'The major version cannot change once the template exists; bump the minor part';
  },
  immutable_template_id: ({ immutableRoot }) => (immutableRoot?.template_id
    ? `The template ID cannot change once the template exists; keep "${immutableRoot.template_id}"`
    : 'The template ID cannot change once the template exists'),
  invalid_version: () => 'Use a dotted numeric version, e.g. 1.0',
  invalid_template_id: () => 'Template ID is required',
  unknown_field: ({ error }) => `Remove the unsupported property "${quoted(error.detail)}"`,
  unknown_node_type: ({ error }) => `Unsupported node type "${quoted(error.detail)}"; use tab or one of ${TEMPLATE_FIELD_TYPES.join(', ')}`,
  max_tab_depth: () => `Tabs can be nested at most ${MAX_TEMPLATE_TAB_DEPTH} levels deep`,
  field_not_found: () => 'This field no longer exists in the document',
  invalid_path: () => 'The field path is not valid',
};

const stripPathPrefix = (error) => {
  const prefix = `${pathKey(error.path) || 'root'}: `;
  return error.detail?.startsWith(prefix) ? error.detail.slice(prefix.length) : error.detail || 'Request failed.';
};

/**
 * Finds the node an error path points at. Paths may run past a node into one
 * of its properties (the backend reports `<node>.validation` for limit
 * problems); the unresolved tail names the inspector control instead.
 */
export const resolveTemplateErrorPath = (document, path = []) => {
  const nodePath = [];
  let children = document?.children || [];
  for (const key of path) {
    const node = children.find((item) => item.key === key);
    if (!node) break;
    nodePath.push(key);
    children = node.children || [];
  }
  const rest = path.slice(nodePath.length);
  return { nodePath, node: nodePath.length ? getNodeAtPath(document, nodePath) : null, field: rest[0] };
};

const FIELD_BY_CODE = {
  min_value: 'default_value',
  max_value: 'default_value',
  invalid_number: 'default_value',
  invalid_string: 'default_value',
  invalid_time: 'default_value',
  invalid_datetime: 'default_value',
  invalid_boolean: 'default_value',
  invalid_json: 'default_value',
  duplicate_key: 'key',
  key_required: 'key',
  name_required: 'human_readable_name',
  unknown_node_type: 'type',
  invalid_validation: 'validation',
  invalid_version: 'version',
  immutable_major_version: 'version',
  invalid_template_id: 'template_id',
  immutable_template_id: 'template_id',
};

/**
 * Turns a {path, code, detail} error into what the editor needs: the node it
 * belongs to, the inspector control (`field`) and the operator wording.
 */
export const describeTemplateError = (document, error, { immutableRoot } = {}) => {
  const { nodePath, node, field: pathField } = resolveTemplateErrorPath(document, error.path);
  const field = error.field || (pathField === 'validation' ? 'validation' : FIELD_BY_CODE[error.code]);
  const copy = TEMPLATE_ERROR_COPY[error.code];
  const message = copy ? copy({ error, node, document, immutableRoot }) : stripPathPrefix(error);
  return { ...error, nodePath, field, message };
};

const draftKey = (parameterId) => `template-editor-draft:v2:${parameterId || 'new'}`;

export const loadTemplateDraft = (parameterId) => {
  if (typeof window === 'undefined') return null;
  try {
    const draft = JSON.parse(window.sessionStorage.getItem(draftKey(parameterId)));
    return draft?.schemaVersion === TEMPLATE_DRAFT_SCHEMA_VERSION ? draft : null;
  } catch {
    return null;
  }
};

export const saveTemplateDraft = (parameterId, values, jsonSource, baseSignature) => {
  if (typeof window === 'undefined') return;
  let sensitiveSource = /"(?:is_sensitive|\$masked)"/.test(jsonSource || '');
  try {
    sensitiveSource = hasSensitiveValues(JSON.parse(jsonSource || '{}')) || /"\$masked"/.test(jsonSource || '');
  } catch { /* Keep drafts with ambiguous sensitivity out of storage until their JSON is valid. */ }
  if (hasSensitiveValues(values) || sensitiveSource) {
    window.sessionStorage.removeItem(draftKey(parameterId));
    return;
  }
  window.sessionStorage.setItem(draftKey(parameterId), JSON.stringify({
    schemaVersion: TEMPLATE_DRAFT_SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    ...(baseSignature !== undefined && { baseSignature }),
    values,
    ...(jsonSource !== undefined && { jsonSource }),
  }));
};

export const clearTemplateDraft = (parameterId) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(draftKey(parameterId));
};


// Reuse the existing non-sensitive draft; only add its accepted job identity.
// A later save replaces this marker, so completing an older job cannot erase
// a newer draft. No parameter payload is copied into the job tracker.
export const markTemplateDraftAssignment = (parameterId, jobId, ownerId) => {
  if (typeof window === 'undefined' || ownerId == null) return;
  const draft = loadTemplateDraft(parameterId);
  if (!draft) return;
  try {
    window.sessionStorage.setItem(draftKey(parameterId), JSON.stringify({
      ...draft, assignment: { jobId, ownerId },
    }));
  } catch { /* Draft storage is optional. */ }
};

export const completeTemplateDraftAssignment = (jobId, ownerId) => {
  if (typeof window === 'undefined' || ownerId == null) return;
  try {
    const keys = Object.keys(window.sessionStorage).filter((key) => key.startsWith('template-editor-draft:v2:'));
    keys.forEach((key) => {
      const draft = JSON.parse(window.sessionStorage.getItem(key));
      if (draft?.assignment?.jobId === jobId && String(draft.assignment.ownerId) === String(ownerId)) {
        window.sessionStorage.removeItem(key);
      }
    });
  } catch { /* Invalid/unavailable optional draft storage must not break jobs. */ }
};
